import { useMemo } from "react";
import { LuChevronLeft, LuFileText, LuMailCheck, LuSave, LuShare2, LuTimer } from "react-icons/lu";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { useJob, useOpenJobs } from "../api";
import CareersShell, { TopBar } from "../components/CareersShell";
import JobDescription from "../components/JobDescription";
import RoleList, { RoleIcon, RoleListSkeleton, RoleMeta } from "../components/RoleList";
import ShareActions, { shareJob, useCopied } from "../components/ShareActions";
import { isClosed, parseApiDate } from "../lib/dates";
import { summarise } from "../lib/description";
import { hasProgress, loadDraft } from "../lib/draft";
import { careersPath } from "../lib/paths";
import { careersShareUrl } from "../lib/share";
import useDocumentMeta from "../lib/useDocumentMeta";

function BackLink() {
  const { state } = useLocation();
  const navigate = useNavigate();
  // Coming from the list, Back keeps the list's search, filter and scroll.
  if (state?.fromList) {
    return (
      <button type="button" onClick={() => navigate(-1)}>
        <LuChevronLeft aria-hidden="true" /> All roles
      </button>
    );
  }
  return (
    <Link to={careersPath()}>
      <LuChevronLeft aria-hidden="true" /> All roles
    </Link>
  );
}

function jobPostingSchema(job) {
  const expiry = parseApiDate(job.expiry_date);
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description || job.title,
    datePosted: job.created_at?.slice(0, 10),
    ...(expiry && { validThrough: `${expiry.toLocaleDateString("en-CA")}T23:59:59+04:00` }),
    hiringOrganization: { "@type": "Organization", name: "Nando's UAE" },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "AE" } },
    directApply: true,
  };
}

function useJobMeta(job, slug) {
  const meta = useMemo(() => {
    if (!job) return { title: "Careers at Nando's UAE" };
    return {
      title: `${job.title} – Careers at Nando's UAE`,
      description: summarise(job.description, 160),
      canonical: careersShareUrl(slug),
      jsonLd: jobPostingSchema(job),
    };
  }, [job, slug]);
  useDocumentMeta(meta);
}

function JobHero({ job }) {
  return (
    <div className="cr-job-hero">
      <div className="cr-wrap cr-job-hero-inner">
        <nav className="cr-crumbs" aria-label="Breadcrumb">
          <BackLink />
        </nav>
        <div className="cr-job-title-row">
          <RoleIcon title={job.title} />
          <div>
            <h1 className="cr-h1">{job.title}</h1>
            <RoleMeta job={job} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplyCard({ applyTo, resuming }) {
  return (
    <div className="cr-card cr-apply-card">
      <h2 className="cr-h2">{resuming ? "Pick up where you left off" : "Apply for this role"}</h2>
      <p className="cr-muted cr-small" style={{ marginTop: 4 }}>
        {resuming
          ? "Your answers are saved on this device."
          : "It takes about five minutes. Your answers save as you go."}
      </p>
      <Link to={applyTo} className="cr-btn cr-btn--primary cr-btn--block cr-btn--lg">
        {resuming ? "Continue application" : "Apply now"}
      </Link>
      <ul className="cr-facts" aria-label="What you'll need">
        <li>
          <LuFileText aria-hidden="true" />
          <span>Your CV as a PDF or Word file, up to 5 MB</span>
        </li>
        <li>
          <LuMailCheck aria-hidden="true" />
          <span>An email address to confirm with a 4-digit code</span>
        </li>
        <li>
          {resuming ? <LuSave aria-hidden="true" /> : <LuTimer aria-hidden="true" />}
          <span>
            {resuming ? "Nothing to retype" : "Leave any time and pick up where you left off"}
          </span>
        </li>
      </ul>
    </div>
  );
}

function MobileApplyBar({ job, applyTo, resuming }) {
  const [copied, setCopied] = useCopied();
  return (
    <div className="cr-mobile-bar">
      <Link to={applyTo} className="cr-btn cr-btn--primary cr-btn--lg">
        {resuming ? "Continue application" : "Apply now"}
      </Link>
      <button
        type="button"
        className="cr-btn cr-btn--secondary cr-btn--lg"
        onClick={() => shareJob(job, () => setCopied(true))}
        aria-label={copied ? "Link copied" : "Share this role"}
      >
        <LuShare2 />
      </button>
    </div>
  );
}

function MoreRoles({ currentId, departmentId }) {
  const { data: jobs = [], status } = useOpenJobs();
  const others = jobs
    .filter((j) => j.id !== currentId)
    .sort((a, b) => (b.department?.id === departmentId) - (a.department?.id === departmentId))
    .slice(0, 3);

  if (status === "pending") return <RoleListSkeleton rows={2} />;
  if (!others.length) return null;
  return (
    <section className="cr-more-roles" aria-labelledby="cr-more-title">
      <h2 id="cr-more-title" className="cr-h2">
        More roles at Nando's
      </h2>
      <RoleList jobs={others} withSummary={false} label="More roles" />
    </section>
  );
}

function ClosedRole() {
  return (
    <div className="cr-closed">
      <nav className="cr-crumbs" aria-label="Breadcrumb">
        <BackLink />
      </nav>
      <h1 className="cr-h1">This role has closed</h1>
      <p className="cr-muted">It's no longer taking applications. Here's what's open right now.</p>
    </div>
  );
}

function LoadingJob() {
  return (
    <div className="cr-wrap" aria-busy="true" style={{ paddingTop: 24 }}>
      <span className="cr-skeleton" style={{ height: 14, width: 80 }} />
      <span className="cr-skeleton" style={{ height: 32, width: "50%", marginTop: 16 }} />
      <span className="cr-skeleton" style={{ height: 14, width: 220, marginTop: 14 }} />
      <span className="cr-visually-hidden">Loading role</span>
    </div>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="cr-wrap" style={{ paddingTop: 32 }}>
      <div className="cr-state" role="alert">
        <h1 className="cr-h2">We couldn't load this role</h1>
        <p className="cr-muted">Check your connection and try again.</p>
        <button type="button" className="cr-btn cr-btn--primary" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

function OpenRole({ job, slug }) {
  // An old link by numeric id (/job-view/6) settles on the role's own URL.
  if (job.slug && job.slug !== slug) return <Navigate to={careersPath(job.slug)} replace />;
  const resuming = hasProgress(loadDraft(slug));
  const applyTo = careersPath(slug, "apply");
  return (
    <>
      <JobHero job={job} />
      <div className="cr-wrap">
        <div className="cr-job-grid">
          <JobDescription text={job.description} title={job.title} />
          <aside className="cr-aside" aria-label="Apply">
            <ApplyCard applyTo={applyTo} resuming={resuming} />
            <div className="cr-card">
              <ShareActions job={job} />
            </div>
          </aside>
        </div>
        <MoreRoles currentId={job.id} departmentId={job.department?.id} />
      </div>
      <MobileApplyBar job={job} applyTo={applyTo} resuming={resuming} />
    </>
  );
}

function JobContent({ query, slug }) {
  const { data: job, status, refetch } = query;
  if (status === "pending") return <LoadingJob />;
  if (status === "error") return <LoadError onRetry={refetch} />;
  if (!job || isClosed(job.expiry_date)) {
    return (
      <div className="cr-wrap">
        <ClosedRole />
        <MoreRoles currentId={job?.id} />
      </div>
    );
  }
  return <OpenRole job={job} slug={slug} />;
}

export default function JobPage() {
  const { slug } = useParams();
  const query = useJob(slug);
  const open = query.data && !isClosed(query.data.expiry_date);
  useJobMeta(open ? query.data : null, slug);

  return (
    <CareersShell className={open ? "cr-has-mobile-bar" : ""}>
      <TopBar />
      <main id="cr-main" className="cr-main">
        <JobContent query={query} slug={slug} />
      </main>
    </CareersShell>
  );
}
